import type { CrmNoteType, InsInsightOutcomeResponse } from '@plato/data/prisma';
import type { SrInsightType as InsightType } from '@plato/data/starrocks/types';
import type { SearchEntity, SearchMatchReason, SearchMatchType } from '@plato/search/search-types';
import type { UiFilterVisibility } from '@plato/ui-legacy/components/filters/types/ui-filter-visibility';
import type { CrmTaskIntent } from '../../../server/dto/crm-task-input-dto.ts';
import type { AiChatReplyStatus } from '../../../server/utils/ai-chat-reply-status.ts';
import type { ConversationRecencyGroup } from '../../ai-chat/utils/conversation-recency.ts';

export type FilterEventPayload = {
    filters: string[];
};

export type SortEventPayload = {
    /** The name of the column that was sorted. */
    column: string;
    direction: 'ASC' | 'DESC';
};

export type SalesInsightEventPayload = {
    customer_id: string;
    insight_ids_visible: string[];
    position_within_filter: number;
    rank: number;
    render_id: string;
    sales_insight_id: string;
    sales_insight_type: InsightType;
};

type SalesInsightOutcomeEventPayload = SalesInsightEventPayload & {
    reason_given_dropdown: string;
    reasons_given_dropdown: string[];
    reason_given_text: string;
};

export type SalesInsightEvents = {
    /**
     * Triggered when the list of insights is rendered (on page load and filter change).
     */
    sales_insight_list_rendered: {
        customer_id: string;
        insight_ids: string[];
        insight_types_filter: InsightType[];
        render_id: string;
    };

    /**
     * Triggered when a user views a sales insight on a customer
     * detail page once it reaches the scroll area.
     * */
    sales_insight_viewed: SalesInsightEventPayload;

    /**
     * Triggered when a user clicks to expand and
     * see more details about a specific sales insight.
     * */
    sales_insight_expanded: SalesInsightEventPayload;

    /**
     * Triggered when a user marks a recommendation as "Relevant"
     * (indicating they followed or acted on the sales insight).
     * */
    sales_insight_completed: SalesInsightOutcomeEventPayload;

    /**
     * Triggered when a user marks a sales insight as "not relevant"
     * (helpful for understanding why some insights aren't working).
     * */
    sales_insight_not_relevant: SalesInsightOutcomeEventPayload;

    /**
     * Triggered when a user opens the actions dropdown
     * */
    sales_insight_actions_opened: SalesInsightEventPayload;

    /**
     * Triggered when a user clicks on the category of the insight card
     * */
    sales_insight_actionable_entity_clicked: SalesInsightEventPayload;

    /**
     * Triggered when a user clicks on a product in the expanded insight view
     * */
    sales_insight_expanded_entity_clicked: SalesInsightEventPayload & {
        clicked_product_id: string;
        clicked_product_name: string;
        clicked_product_position: number;
        visible_products: { product_id: string; product_name: string }[];
    };

    /**
     * Triggered when a user changes the sort order of the sales insights list
     * */
    sales_insight_sort_dropdown_clicked: {
        from_value: string;
        to_value: string;
    };

    /**
     * Triggered when a user expands the insights section (progressive disclosure)
     * to see more than the initial 3 insights on the customer detail page.
     */
    sales_insight_section_expanded: {
        customer_id: string;
        section: 'risks' | 'opportunities';
        total_count: number;
        initial_visible_count: number;
    };
};

export type TransactionAddedItemEventPayload = {
    draft_id: string;
    product_variant_id: string;
    article_group_id?: string;
    quantity?: number;
    /** Set when the product came from a sales insight's suggestions */
    sales_insight_id?: string;
};

export type TransactionAddedItemEventSource = 'catalogue' | 'product_detail_page' | 'recommended' | 'search';

export type TransactionCreationStartedEventSource =
    | 'customer_detail'
    | 'drafts_list'
    | 'navigation_menu'
    | 'order_detail'
    | 'order_list'
    | 'quote_detail'
    | 'quote_list';

export type TransactionsEvents = {
    /**
     * Triggered when a user initiates the process of creating a new quote or order. This does not include editing drafts.
     * */
    transaction_creation_started: {
        customer_id?: string;
        source: TransactionCreationStartedEventSource;
    };

    /**
     * Triggered when draft is saved or updated. This is also captured when a draft is saved in case the users drop off.
     * */
    transaction_draft_deleted: {
        draft_id: string;
    };

    /**
     * Triggered when a user submits a quote or order to the ERP system.
     * */
    quote_order_submitted: {
        draft_id: string;
        quote_order_type: string;
        number_of_items: number;
        total_sum: number;
        customer_id: string;
    };

    /**
     * Triggered when a user adds an item from the product catalogue.
     * */
    item_added_from_catalogue: TransactionAddedItemEventPayload;

    /**
     * Triggered when a user adds an item from the product detail drawer, e.g. a related item recommendation.
     * */
    item_added_from_product_detail_page: TransactionAddedItemEventPayload;

    /**
     * Triggered when a user adds an item using the recommended products selector.
     * */
    item_added_from_recommended: TransactionAddedItemEventPayload;

    /**
     * Triggered when a user adds an item using the search box.
     * */
    item_added_from_search: TransactionAddedItemEventPayload;

    /**
     * Triggered when a user opens the quote/order detail drawer.
     */
    quote_order_detail_viewed: {
        customer_id: string;
        quote_order_type: string;
        transaction_id: string;
    };

    /**
     * Triggered when a filter is used on the quote/order list page
     */
    quote_order_list_filter_used: FilterEventPayload;

    /**
     * Triggered when a column is sorted on the quote/order list page
     */
    quote_order_list_sort_used: SortEventPayload;

    /**
     * Triggered when a filter is used on the drafts list page
     */
    drafts_list_filter_used: FilterEventPayload;

    /**
     * Triggered when a column is sorted on the drafts list page
     */
    drafts_list_sort_used: SortEventPayload;
};

type DraftLineItemEventPayload = {
    draft_id: string;
    line_item_id: string;
    product_variant_id: string;
};

/**
 * Line-item fields whose user edits we report via `draft_line_item_field_changed`.
 * Source of truth for both the event's `field` type and the tracked-fields list
 * passed to `useTrackedFieldChanges` on the line-item row.
 */
export const draftLineItemFields = ['quantity', 'price', 'rebate'] as const;

export type DraftLineItemField = (typeof draftLineItemFields)[number];

export type DraftLineItemEvents = {
    /**
     * Triggered when a user edits a field on a draft line item — inline on the
     * row, or via the line item edit dialog. Fires once per changed field.
     */
    draft_line_item_field_changed: DraftLineItemEventPayload & {
        field: DraftLineItemField;
    };

    /**
     * Triggered when a user clicks the "Edit" action on a draft line item to open
     * the full line item edit dialog.
     */
    draft_line_item_edit_more_clicked: DraftLineItemEventPayload;
};

/**
 * Draft header fields whose user edits we report via `draft_header_field_changed`.
 * Source of truth for both the event's `field` type and the tracked-fields list
 * passed to `useTrackedFieldChanges` on the header page.
 */
export const draftHeaderFields = [
    'billing_address_id',
    'shipping_address_id',
    'contact_person_id',
    'delivery_date',
    'due_date',
    'document_type',
    'shipping_type_id',
    'po_number',
    'notes',
] as const;

export type DraftHeaderField = (typeof draftHeaderFields)[number];

export type DraftHeaderEvents = {
    draft_header_field_changed: {
        draft_id: string;
        field: DraftHeaderField;
    };
};

/**
 * Identifies the UI location a task was created from. Each call site of the
 * task-create flow must pass one of these values so we can attribute adoption
 * per surface in PostHog. Add a new value whenever you introduce a new entry
 * point — the required `source` property will flag missing coverage at
 * compile time.
 */
export type TaskCreatedSource =
    | 'customer_activity'
    | 'customer_detail'
    | 'customer_list'
    | 'dashboard'
    | 'event_details'
    | 'navigation_menu'
    | 'note_options'
    | 'order_list'
    | 'quote_list'
    | 'quote_order_detail'
    | 'sales_insight'
    | 'task_deleted_toast'
    | 'task_overview_page'
    | 'team_header'
    | 'team_user'
    | 'tender_win';

/** Identifies the UI location where a task was deleted. */
export type TaskDeletedSource = 'task_detail' | 'task_overview_page';

/** Identifies the UI location where a task was marked as completed. */
export type TaskCompletedSource =
    | 'activities_page'
    | 'customer_detail'
    | 'dashboard'
    | 'log_interaction_dialog'
    | 'note_edit_dialog'
    | 'task_detail'
    | 'task_overview_page';

/** Identifies the UI location where a task was edited. */
export type TaskUpdatedSource = 'log_interaction_dialog' | 'task_detail';

type TaskEventPayload = {
    task_id: string;
    due_date: string;
    assignee_id: string | undefined;
    assignor_id: string | undefined;
    is_assigned_to_oneself: boolean;
    related_entity_type: string | undefined;
    related_entity_id: string | undefined;
};

export type TaskEvents = {
    /**
     * Triggered when a user creates a new task (either for themselves or someone else).
     * */
    task_created: TaskEventPayload & { source: TaskCreatedSource };

    /**
     * Triggered when a user assigns one task to several colleagues in a single action.
     * Sent once instead of a task_created per assignee. Picking a single assignee
     * sends task_created instead, so this event always covers more than one task.
     * */
    tasks_bulk_created: {
        task_ids: string[];
        assignee_ids: string[];
        task_count: number;
        assignor_id: string | undefined;
        due_date: string;
        is_assigned_to_oneself: boolean;
        related_entity_type: string | undefined;
        related_entity_id: string | undefined;
        source: TaskCreatedSource;
    };

    /**
     * Triggered when a user views a task (either from the standalone tasks page, the customer detail page, or the home dashboard snippet).
     * */
    task_viewed: TaskEventPayload;

    /**
     * Triggered when a task is marked as completed (either for themselves or someone else).
     * From the log-interaction modal it is sent only once the log is saved, and carries the
     * modal's `log_session_id`.
     * */
    task_completed: TaskEventPayload & Partial<LogInteractionContext> & { source: TaskCompletedSource };

    /**
     * Triggered when a task is deleted. `deletion_type` is always `user_initiated`
     * today — a cascade path would send `cascade` from the server.
     * */
    task_deleted: TaskEventPayload & {
        source: TaskDeletedSource;
        was_completed: boolean;
        deletion_type: 'user_initiated';
    };

    /**
     * Triggered on every successful task save, carrying the task's full state after the
     * save. What changed is derived in PostHog by comparing consecutive events per task.
     * Description content is never sent. Completions additionally send `task_completed`.
     * From the log-interaction modal it is sent only once the log is saved, and carries the
     * modal's `log_session_id`.
     * */
    task_updated: TaskEventPayload &
        Partial<LogInteractionContext> & {
            changed_by_id: string;
            source: TaskUpdatedSource;
        };

    /**
     * Triggered when a filter is used on the task overview page
     */
    task_overview_filter_used: FilterEventPayload;

    /**
     * Triggered when a column is sorted on the task overview page
     */
    task_overview_sort_used: SortEventPayload;
};

export type LogInteractionPageSource = 'customer_detail' | 'side_menu' | 'event_drawer';

/**
 * Stamps an event with the log-interaction modal it happened in, so one visit's actions read
 * as a sequence. Spread in at the call site; the dialog's older `log_date_prefill_*` and
 * `create_next_event_from_log` still carry none.
 */
export type LogInteractionContext = {
    /** One modal instance, from open to submit or discard. */
    log_session_id: string;
    page_source?: LogInteractionPageSource;
};

type CustomerInteractionEventPayload = {
    note_id: string;
    customer_id: string;
    note_type: string;
    ai_session_id?: string;
    page_source?: LogInteractionPageSource;
};

type CustomerInteractionCreatedEvent = {
    not_reached_mode?: boolean;
    had_reason?: boolean;
    task_count?: number;
    /** Existing tasks edited in the modal, completions included. */
    task_update_count?: number;
    /** Existing tasks completed in the modal, a subset of `task_update_count`. */
    task_completed_count?: number;
    log_session_id: string;
} & CustomerInteractionEventPayload;

export type CustomerNoteEvents = {
    /**
     * Triggered when the log-interaction modal (`LogInteractionDialog`) opens, from any of
     * its three entry points. Denominator for the funnel's abandonment rate — pairs with
     * `customer_interaction_created` (completed) and `log_interaction_discarded` (abandoned).
     * */
    log_interaction_opened: LogInteractionContext;

    /**
     * Triggered when a user picks the note type (Besuch / Anruf / Notiz) in the
     * log-interaction modal. Not fired when opened from a calendar event, since the type
     * selector is hidden and implicitly 'VISIT' there.
     * */
    log_interaction_type_selected: LogInteractionContext & {
        note_type: CrmNoteType;
    };

    /**
     * Triggered when a user starts a task from the log-interaction form — the moment the
     * task form opens, before anything is filled in. Paired with
     * `log_interaction_task_added` it measures how many started tasks never make it into
     * the log, which no other event can see: a rep who opens the form and gives up sends
     * nothing at all.
     * */
    log_interaction_task_started: LogInteractionContext & {
        /** What the entry point pre-selects; the rep can still change it in the form. */
        task_intent: CrmTaskIntent | undefined;
        /** Same provenance as `log_interaction_task_added`, so the two can be compared. */
        source: 'open_quotes' | 'tasks_section' | 'ai_insights';
    };

    /**
     * Triggered when a user adds a task to the log-interaction form, via one of the
     * quick-action pills. The task is not persisted yet — it is written when the
     * interaction is submitted, so there is no `note_id` or task id at this point, and a
     * task added to a form that is later discarded only ever shows up here.
     * */
    log_interaction_task_added: LogInteractionContext & {
        task_intent: CrmTaskIntent | undefined;
        assignee_count: number;
        is_assigned_to_oneself: boolean;
        /** Provenance, not identity: a task links to a set of entities, so no single id here
         * could name the one the rep acted on. */
        source: 'open_quotes' | 'tasks_section' | 'ai_insights';
    };

    /**
     * Triggered when a user opens or closes the Open quotes section. It starts closed, so the
     * first `expanded: true` is the rep choosing to look at the quotes at all.
     * */
    log_quote_section_toggled: LogInteractionContext & {
        customer_id: string;
        expanded: boolean;
        /** What the section listed, capped at the five it fetches; absent if still loading. */
        quote_count?: number;
        draft_count?: number;
    };

    /**
     * Triggered when a user opens a quote card to read its positions — engagement short of
     * acting on the quote, and what fetches them.
     * */
    log_quote_expanded: LogInteractionContext & {
        customer_id: string;
        /** The quote's ERP `sales_order_id`, not its display id. */
        quote_id: string;
        /** What the section listed, capped at the five it fetches. */
        quote_count: number;
    };

    /**
     * Triggered when a user opens or closes the AI insights section. It starts closed, so the
     * first `expanded: true` is the rep choosing to look at the insights at all.
     * */
    log_insight_section_toggled: LogInteractionContext & {
        customer_id: string;
        expanded: boolean;
        /** Churn-warning insights the section listed; absent while still loading. */
        risk_count?: number;
        /** Non-churn (opportunity) insights the section listed; absent while still loading. */
        opportunity_count?: number;
    };

    /**
     * Triggered when a user reveals the rest of a section via "show more" — the risks or
     * opportunities list starts capped at the first few, so this is the rep choosing to look
     * past them. `total_count` is how many that group holds in full.
     * */
    log_insight_section_expanded: LogInteractionContext & {
        customer_id: string;
        section: 'risks' | 'opportunities';
        total_count: number;
    };

    /**
     * Triggered when a user opens a single insight card to read its detail — engagement short
     * of recording an outcome. The log-interaction counterpart to the generic
     * `sales_insight_expanded`, carrying the interaction session so it joins the log funnel.
     * */
    log_insight_card_expanded: LogInteractionContext & {
        customer_id: string;
        sales_insight_id: string;
    };

    /**
     * Triggered when a user records an outcome for an insight in the AI insights section —
     * picking how the customer responded. The outcome is staged with the interaction, so it is
     * only persisted when the interaction is submitted; a selection on a form later discarded
     * only ever shows up here.
     * */
    log_insight_outcome_selected: LogInteractionContext & {
        customer_id: string;
        sales_insight_id: string;
        response: InsInsightOutcomeResponse;
    };

    /**
     * Triggered when a user clears a previously staged outcome ("None"). The counterpart to
     * `log_insight_outcome_selected`: without it, a rep who records then clears an outcome
     * reads the same as one who never touched it.
     * */
    log_insight_outcome_cleared: LogInteractionContext & {
        customer_id: string;
        sales_insight_id: string;
    };

    /**
     * Triggered when a user closes the log-interaction modal without saving (X, Escape, or
     * confirming the discard-changes prompt). `had_content` distinguishes an empty walk-away
     * from a discarded in-progress note.
     * */
    log_interaction_discarded: LogInteractionContext & {
        had_content: boolean;
    };

    /**
     * Triggered when a user creates a new customer note.
     * */
    customer_interaction_created: CustomerInteractionCreatedEvent;

    /**
     * Triggered when a user views a customer note.
     * */
    customer_interaction_viewed: CustomerInteractionEventPayload;

    /**
     * Triggered when a user pins a customer note.
     * */
    customer_note_pinned: {
        note_id: string;
        customer_id: string;
        note_type: CrmNoteType;
    };

    /**
     * The log-interaction date/time was auto-filled from the customer's last event and the
     * user submitted without changing either field.
     * */
    log_date_prefill_used: {
        event_id: string;
        days_ago: number;
        interaction_type: 'besuch' | 'anruf';
    };

    /**
     * The date/time was auto-filled but the user changed the date, the time, or both before
     * submitting.
     * */
    log_date_prefill_overridden: {
        event_id: string;
        days_ago: number;
        fields_changed: 'date' | 'time' | 'both';
    };

    /**
     * Tog-interaction form was opened for a customer with no recent event to prefill from.
     * */
    log_date_prefill_unavailable: {
        customer_id: string;
    };
};

export type SalesReportEvents = {
    /**
     * Triggered when a user views the sales report feature (either from the standalone sales reports page or the customer detail page tab).
     * */
    sales_report_viewed: {
        page_source: 'customer' | 'product' | 'standalone';
    };

    /**
     * Triggered when a user applies one or more filters to a sales report.
     * */
    sales_report_filters_applied: {
        filters: string[];
        number_of_filters: number;
    };

    /**
     * Triggered when a user sorts a column in a sales report.
     * */
    sales_report_sort_used: SortEventPayload;

    /**
     * Triggered when a user opens the metric FAQ from the settings menu
     * (REQ-3777). Measures how often the "why does this differ from my ERP?"
     * question is asked in product rather than at support.
     */
    metric_explainer_opened: {
        source: 'settings_menu';
    };
};

type ProductDetailEventPayload = {
    item_id: string;
};

type RelatedItemViewedPayload = {
    source_item_id: string;
    item_id: string;
    rank: number;
};

type RelatedItemClickedPayload = {
    source_item_id: string;
    item_id: string;
};

type RelatedItemCarouselScrolledPayload = {
    source_item_id: string;
    loaded_items: string[];
};

export type ProductDetailEvents = {
    /**
     * Triggered when a user opens an item detail page.
     * */
    item_detail_page_viewed: ProductDetailEventPayload;

    /**
     * Triggered when a user views an individual related item recommendation.
     * */
    related_item_viewed: RelatedItemViewedPayload;

    /**
     * Triggered when a user scrolls the carousel that contains the related item recommendations.
     * */
    related_item_carousel_scrolled: RelatedItemCarouselScrolledPayload;

    /**
     * Triggered when a user clicks on a related item recommendation.
     * */
    related_item_clicked: RelatedItemClickedPayload;
};

/**
 * Identifies the UI surface an event-create-dialog action originated from. Shared
 * across event_created, event_create_dialog_opened, and event_create_dialog_discarded
 * so all three can be sliced by the same entry point.
 */
export type EventCreatedSource = 'customer' | 'recommendation' | 'calendar' | 'dashboard' | 'menu';

/**
 * Identifies the surface a user came from when they opened the event they went on to edit.
 * Edits are only reachable through the event details dialog, so this records what opened that.
 */
export type EventUpdatedSource = 'calendar' | 'customer_detail' | 'dashboard' | 'event_created_toast';

export type EventEvents = {
    /**
     * Triggered when a new event is created
     */
    event_created: {
        is_recurring: boolean;
        has_set_time: boolean;
        source: EventCreatedSource;
        customer_id?: string;
        has_customer: boolean;
        address_changed: boolean;
        invitees_added: boolean;
        has_agenda: boolean;
    };

    /**
     * Triggered when the event-create dialog opens, from any entry point.
     */
    event_create_dialog_opened: {
        source: EventCreatedSource;
        has_customer: boolean;
    };

    /**
     * Triggered when a user closes a dirty event-create dialog via the discard
     * confirmation prompt. `confirmed: true` means they discarded the draft;
     * `confirmed: false` means they chose to keep editing. Only fires when the
     * confirmation prompt was actually shown (i.e. the form had unsaved changes) —
     * closing an untouched form is not a discard.
     */
    event_create_dialog_discarded: {
        source: EventCreatedSource;
        confirmed: boolean;
    };

    /**
     * Triggered when a user steps or resets the visible date range on the events
     * calendar (previous/next arrows, the "Today" button, the date picker on the title, or the day strip).
     */
    event_calendar_period_changed: {
        direction: 'previous' | 'next' | 'today' | 'picker' | 'day_strip' | 'day_swipe';
        view_mode: 'day' | 'week';
    };

    /**
     * Triggered when an existing event is updated
     */
    event_updated: {
        is_recurring: boolean;
        has_set_time: boolean;
        source: EventUpdatedSource;
        has_customer: boolean;
        address_changed: boolean;
        invitees_added: boolean;
        has_agenda: boolean;
    };

    /**
     * Triggered when an event is clicked from anywhere in the app
     */
    event_clicked: {
        is_recurring: boolean;
        has_set_time: boolean;
    };

    /**
     * Triggered when an existing event is deleted
     */
    event_deleted: {
        is_recurring: boolean;
        has_set_time: boolean;
    };

    /**
     * Triggered when an event is reordered in the event list view
     */
    event_reordered: {
        is_recurring: boolean;
        has_set_time: boolean;
    };

    event_recommendation_clicked: {
        customer_id: string;
    };

    event_recommendation_preview: {
        customer_id: string;
    };

    /**
     * Triggered when a rep dismisses a calendar suggestion via the "what's wrong with this
     * recommendation?" modal. `sub_option` is set for the
     * "not visited" reason, `frequency_weeks` for the "suggested too often" reason.
     */
    event_recommendation_hidden: {
        customer_id: string;
        reason: string;
        sub_option?: string;
        frequency_weeks?: number;
    };

    /**
     * Triggered when a sales leader selects a team member to view their events/calendar.
     */
    event_team_member_calendar_viewed: {
        selected_user_id: string;
    };

    /**
     * Triggered when a user opens the day's route in Google Maps.
     */
    route_opened_in_maps: {
        event_count: number;
        addresses_count: number;
        date: string;
    };

    /**
     * Triggered when a user clicks the "Schedule next visit" fake door button
     * on the event log screen. Used to measure demand for this feature.
     */
    create_next_event_from_log: {
        customer_id: string;
        note_id?: string;
    };
};

export type CustomerListEvents = {
    /**
     * Triggered when a filter is used on the customer list page
     */
    customer_list_filter_used: FilterEventPayload;

    /**
     * Triggered when a column is sorted on the customer list page
     */
    customer_list_sort_used: SortEventPayload;

    /**
     * Triggered when a user opens the header tooltip of one of the manager-view
     * visit columns on the customer list page. Sorting these columns is captured
     * by `customer_list_sort_used` (the column name identifies the visit column).
     */
    customer_list_visit_column_tooltip_opened: {
        column: 'visits_per_100k' | 'last_visit';
        view_mode: 'MANAGER' | 'REP';
    };
};

/**
 * Which composer/field invoked voice input. `source` names the composer's
 * entity family, consistent with its write events (`customer_interaction_created`,
 * `event_created`, `task_created`, `draft_line_item_field_changed`,
 * `sales_insight_completed`). Customer interactions additionally carry the
 * same `note_type` as `customer_interaction_created` for precision
 * (Besuch / Anruf / Notiz).
 */
export type VoiceInputPayload =
    | { source: 'customer_interaction'; note_type: CrmNoteType }
    | { source: 'event' }
    | { source: 'task' }
    | { source: 'comment' }
    | { source: 'sales_insight_outcome' }
    | { source: 'draft_line_item' }
    | { source: 'ask_plato' };

export type VoiceInputEvents = {
    /**
     * Triggered when microphone recording has actually started (not merely when
     * the recording dialog opened — mic permission may still be denied).
     */
    voice_recording_started: VoiceInputPayload;
    /**
     * Triggered when voice transcription completes and a non-empty transcript is returned.
     * `audio_duration_s` is what Deepgram decoded; compare with `recording_duration_ms`
     * to spot audio lost on the device.
     */
    voice_transcription_completed: {
        transcript_text: string;
        character_count: number;
        language: string;
        deepgram_request_id: string;
        audio_duration_s: number;
        confidence: number;
        low_confidence_word_count: number;
        detected_languages: string[];
        model_version: string;
        transcription_latency_ms: number;
    } & VoiceTranscriptionRequestPayload;
    /**
     * Triggered when Deepgram answered but the transcript was empty for a recording
     * longer than a few seconds.
     */
    voice_transcription_empty: {
        deepgram_request_id: string;
        audio_duration_s: number;
    } & VoiceTranscriptionRequestPayload;
    /**
     * Triggered when the recording could not be transcribed. `stage` names where it broke:
     * minting the Deepgram token, reaching Deepgram, or Deepgram rejecting the request.
     */
    voice_transcription_failed: {
        stage: 'token' | 'network' | 'deepgram';
        status_code?: number;
    } & VoiceTranscriptionRequestPayload;
    /**
     * Triggered when the user discards a recording before it is transcribed.
     */
    voice_recording_cancelled: {
        recording_duration_ms: number;
    } & VoiceInputPayload;
};

/** What the client knew about a transcription request, whatever its outcome. */
type VoiceTranscriptionRequestPayload = {
    recording_duration_ms: number;
    mime_type: string;
    blob_size_bytes: number;
    /** `multi` or the fixed locale sent to Deepgram. */
    language_mode: string;
} & VoiceInputPayload;

// Shared payloads for Filter View events
type FilterViewBase = {
    view_id: string;
    view_key: string;
};

type FilterViewWithVisibility = FilterViewBase & {
    visibility: UiFilterVisibility;
};

export type FilterViewEvents = {
    /**
     * Triggered when a user creates a new saved filter view
     */
    filter_view_created: FilterViewWithVisibility;

    /**
     * Triggered when a user updates an existing saved filter view
     */
    filter_view_updated: FilterViewWithVisibility;

    /**
     * Triggered when a user applies/uses a saved filter view
     */
    filter_view_applied: FilterViewWithVisibility;

    /**
     * Triggered once when a user changes the filters while a saved filter view is selected, so the view shows as modified
     */
    filter_view_modified: FilterViewWithVisibility & {
        modified_keys: string[];
    };

    /**
     * Triggered when a user deletes a saved filter view
     */
    filter_view_deleted: FilterViewBase;
};

type AITextFormattingEventPayload = {
    entity_type: string;
    entity_id?: string;
    session_id: string;
};

export type AIWritingEvents = {
    /**
     * Triggered when a user requests AI formatting for their writing
     */
    ai_text_request_formatting: AITextFormattingEventPayload;

    /**
     * Triggered when a user accepts AI formatting suggestions
     */
    ai_text_accept_formatting: AITextFormattingEventPayload;

    /**
     * Triggered when a user rejects AI formatting suggestions
     */
    ai_text_reject_formatting: AITextFormattingEventPayload;

    /**
     * Triggered when a user requests regeneration of AI formatting
     */
    ai_text_regenerate_formatting: AITextFormattingEventPayload;
};

type RecommendedCustomerEventPayload = {
    customer_id: string;
    rank: number;
};

export type RecommendedCustomerEvents = {
    /**
     * Triggered when the recommended customers list is rendered in the events view.
     */
    recommended_customer_list_rendered: {
        customer_ids: string[];
        customer_count: number;
    };

    /**
     * Triggered when a recommended customer card comes into view (intersection observer).
     */
    recommended_customer_viewed: RecommendedCustomerEventPayload;

    /**
     * Triggered when a user clicks on a recommended customer card to view details.
     */
    recommended_customer_clicked: RecommendedCustomerEventPayload;

    /**
     * Triggered when a user clicks the "+" button to schedule an event for a recommended customer.
     */
    recommended_customer_event_scheduled: RecommendedCustomerEventPayload;
};

type TransactionDocumentEventPayload = {
    document_id: string;
    document_type: string;
    document_name: string;
};

export type TransactionDocumentEvents = {
    /**
     * Triggered when a user views a transaction document (PDF) in the viewer modal.
     */
    transaction_document_viewed: TransactionDocumentEventPayload;

    /**
     * Triggered when a user downloads a transaction document (PDF).
     */
    transaction_document_downloaded: TransactionDocumentEventPayload;
};

export type HomeRecommendationsEvents = {
    /**
     * Triggered when user toggles visibility of dormant customers
     * in home dashboard recommendations
     */
    home_recommendations_inactive_toggle_changed: {
        show_inactive: boolean;
        source: 'home_dashboard';
    };
};

export type HomeRecommendationList = 'expansion_potential' | 'revenue_decline';

export type HomeDashboardEvents = {
    /**
     * Triggered when a user clicks one of the home-page KPI tiles
     * (today's sales, today's margin, today's order count, monthly sales).
     */
    home_kpi_tile_clicked: {
        tile: 'today_sales' | 'today_margin' | 'today_order_count' | 'monthly_sales';
    };

    /**
     * Triggered when a user switches between the "open orders" and "open quotes"
     * tabs on the home dashboard.
     */
    home_tab_changed: {
        tab: 'open_orders' | 'open_quotes';
    };

    /**
     * Triggered when a user opens a customer from one of the "Recommended Customers"
     * lists on the home dashboard. The destination pageview alone cannot tell which
     * list (or list position) the customer came from.
     */
    home_recommended_customer_opened: {
        list: HomeRecommendationList;
        customer_id: string;
        /** 1-based position of the card within its list */
        position: number;
    };

    /**
     * Triggered when a user opens a task or event from the "Today" block on the
     * home dashboard. Both open dialogs/drawers, so no pageview is emitted.
     */
    home_activity_item_opened: {
        item_type: 'task' | 'event';
    };

    /**
     * Triggered when a user clicks one of the "show all" / "more" links on the home
     * dashboard sections. The destination pages are also reachable via the side menu,
     * so pageviews alone cannot attribute the navigation to the home section.
     */
    home_show_all_clicked: {
        section:
            | 'tasks'
            | 'events'
            | 'expansion_potential'
            | 'revenue_decline'
            | 'open_orders'
            | 'open_quotes';
    };
};

export type DataFreshnessEvents = {
    /** Triggered when the home dashboard data-freshness warning banner is shown */
    data_freshness_warning_shown: {
        /** Completed working days with no quote/order update */
        days: number;
    };
};

export type CrmFieldEvents = {
    /**
     * Triggered when a user updates a customer contact
     */
    customer_contact_edited: {
        customer_id: string;
        contact_id: string;
    };

    /**
     * Triggered when a user updates a delivery address
     */
    customer_address_edited: {
        customer_id: string;
        address_id: string;
    };

    /**
     * Triggered when a user updates customer fields via the CRM tab
     */
    customer_fields_edited: {
        customer_id: string;
    };

    /**
     * Triggered when a user taps a phone or mobile number link in the customer contacts table.
     */
    customer_contact_phone_clicked: {
        customer_id: string;
        contact_id: string;
        contact_type: 'phone' | 'mobile';
    };

    /**
     * Triggered when a user taps an email link in the customer contacts table.
     */
    customer_contact_email_clicked: {
        customer_id: string;
        contact_id: string;
        contact_type: 'email';
    };
};

export type CustomerModeEvents = {
    /**
     * Triggered when a user toggles Customer View to hide or show gross margins.
     */
    customer_view_toggled: {
        enabled: boolean;
    };
};

export type VersionEvents = {
    /**
     * Triggered when the running client detects the server is on a newer release
     * than the loaded frontend bundle (a stale open tab after a deploy).
     * Fired once per distinct server release per session.
     */
    stale_client_detected: {
        client_release: string;
        server_release: string;
        app_env: string;
    };
};

export type ViewModeEvents = {
    /**
     * Triggered when a manager switches between Manager and Rep view
     * (via the sidebar switch or the manager-view-link confirm modal).
     */
    view_mode_switched: {
        view_mode: 'MANAGER' | 'REP';
    };
};

export type CustomerDetailEvents = {
    /**
     * Triggered when a user opens the customer detail (Customer 360) page.
     */
    customer_detail_page_viewed: {
        customer_id: string;
    };

    /**
     * Triggered when a customer is marked as active.
     */
    customer_reactivated: {
        customer_id: string;
        reason?: string | null;
    };

    /**
     * Triggered when a customer is marked as inactive
     */
    customer_marked_inactive: {
        customer_id: string;
        reason?: string | null;
    };

    /**
     * Triggered when a user clicks "show more" to expand an order or quote
     * in the customer detail activity feed.
     */
    customer_detail_quote_order_expanded: {
        transaction_id: string;
        transaction_type: string;
    };

    /**
     * Triggered when a user clicks the "open orders" or "open quotes" filter
     * link at the top of the customer detail page.
     */
    customer_detail_open_filter_clicked: {
        filter_type: 'open_orders' | 'open_quotes';
    };

    /**
     * Triggered when a user applies or changes filters on the customer detail
     * activity timeline. Reports the set of active filters after the change.
     */
    customer_activity_feed_filter_used: FilterEventPayload;

    /**
     * Triggered when a user opens the customer detail Info tab.
     */
    customer_detail_info_viewed: {
        customer_id: string;
    };
};

type AttachmentContext = 'task' | 'note';

type AttachmentEventPayload = {
    context: AttachmentContext;
    file_type: string;
};

export type AttachmentEvents = {
    /**
     * Triggered when a user successfully uploads an attachment.
     */
    attachment_uploaded: AttachmentEventPayload;

    /**
     * Triggered when an attachment upload fails.
     */
    attachment_upload_failed: AttachmentEventPayload;

    /**
     * Triggered when a user downloads an attachment.
     */
    attachment_downloaded: AttachmentEventPayload;

    /**
     * Triggered when a user deletes/removes an attachment.
     */
    attachment_deleted: AttachmentEventPayload;

    /**
     * Triggered when a user opens the editor to modify an attachment.
     */
    attachment_edited: AttachmentEventPayload;
};

export type GlobalActivityFeedEvents = {
    /**
     * Triggered when a user opens the global activities page. `source` records how
     * the user arrived: via the team-page rep crosslink or directly (nav,
     * bookmark, typed URL).
     */
    global_activity_feed_viewed: {
        source: 'team' | 'direct';
    };

    /**
     * Triggered when a user applies or changes filters on the global activities page.
     * Reports the set of active filters after the change.
     */
    global_activity_feed_filter_used: FilterEventPayload;
};

/**
 * Events for the manager-only Team page (`/team`).
 */

/**
 * Team table cells with a breakdown tooltip — the single source of truth for
 * `team_page_cell_tooltip_opened` payloads and the table's tracking callback.
 */
export type TeamPageCellTooltipColumn =
    | 'customer_contacts'
    | 'recommendations'
    | 'tasks'
    | 'plato_revenue'
    | 'churn_risk'
    | 'opportunities';

export type TeamPageEvents = {
    /**
     * Triggered when a manager opens the Team page.
     */
    team_page_viewed: undefined;

    /**
     * Triggered when a manager changes a filter on the Team page.
     */
    team_page_filter_used: {
        filter: 'search' | 'date' | 'sales_region' | 'role';
    };

    /**
     * Triggered when a manager sorts a column on the Team page.
     */
    team_page_sort_used: SortEventPayload;

    /**
     * Triggered when a manager opens a cell breakdown tooltip on the Team page
     * rep table (the (i) next to a value;.
     */
    team_page_cell_tooltip_opened: {
        column: TeamPageCellTooltipColumn;
    };
};

/**
 * Identifies the UI surface where a search was performed. Each distinct call site
 * gets its own literal so analytics can slice by exact location. Add a new value
 * whenever a new search entry point is introduced — the required `context`
 * property will flag missing coverage at compile time.
 */
export type SearchContextType =
    // Global search widget — emitted from EntitySearchContent itself; all entry
    // points (header button, Cmd+K, TAPI page) share one component instance.
    | 'global'
    // Draft product search inside the line-items page
    | 'draft_line_items'
    // Customer-picker form fields (one per owning entity type)
    | 'draft_customer_select'
    | 'event_customer_select'
    | 'note_customer_select'
    | 'task_linked_entity_select'
    | 'activity_feed_customer_filter';

/**
 * Type of the entity the search is scoped to (the parent record the user is
 * currently creating, editing, or viewing). Undefined when there is no such
 * scope (e.g. the global Cmd+K widget).
 */
export type SearchContextEntityType = 'task' | 'event' | 'draft' | 'note';

/**
 * Base payload attached to every search tracking event. Captures *where* the
 * search happened, *what* it was scoped to, and *what* it was looking for.
 */
export type SearchBasePayload = {
    /** Identifies the UI surface where the search was used. */
    context: SearchContextType;

    /**
     * UUID generated at the moment the search UI opened (component mount).
     * Stable for the lifetime of a single "open" session — all events from the
     * same open share this id.
     */
    context_id: string;

    /**
     * End-customer id when the search happens in customer scope (e.g. creating
     * a task for customer X, taking a note on a customer, drafting an order
     * for a customer). Independent of `context_entity_id` so analytics can
     * answer "what was searched in the context of customer X" without joining.
     */
    customer_id?: string;

    /** Type of the entity that owns the search context (task / event / draft / note). */
    context_entity_type?: SearchContextEntityType;

    /**
     * Id of the context entity. May be undefined when the entity hasn't been
     * created yet (e.g. inside a "create task" dialog before submission).
     */
    context_entity_id?: string;

    /**
     * Entity types being searched for. Plural because the global widget
     * searches customers, products, orders, and quotes in one shot.
     */
    target_entity_types: SearchEntity[];
};

export type SearchResultItem = {
    /**
     * Type of the entity that was found
     */
    entity_type: SearchEntity;

    /**
     * ID of the entity that was found (in the source database)
     */
    entity_id: string;

    /**
     * Random UUID that identifies globally this unique result (different for each request) or the result_id from search service if using new search API
     */
    result_id: string;

    /**
     * Type of the match between the search query and the entity
     */
    match_type: SearchMatchType;
};

type SearchRequestPayload = SearchBasePayload & {
    /**
     * Search-system version for this response.
     */
    version?: string;

    /**
     * The request_id of each entity call, keyed by entity type.
     */
    request_ids?: Partial<Record<SearchEntity, string>>;

    /**
     * Query that was used to search
     */
    search_query: string;

    /**
     * Maximum number of results requested for this query.
     */
    limit: number;

    /**
     * Result offset for this query (0 today; reserved for future pagination).
     */
    offset: number;

    /**
     * Search results
     */
    results: SearchResultItem[];
};

type SearchClickPayload = SearchBasePayload & {
    /**
     * ID of the request that produced this result.
     */
    request_id?: string;

    /**
     * Type of the entity that was found
     */
    result_entity_type: SearchEntity;

    /**
     * ID of the entity that was found (in the source database)
     */
    result_entity_id: string;

    /**
     * Random UUID that identifies globally this unique result (different for each request)
     */
    result_id: string;

    /**
     * Search-system version that produced this result.
     */
    version?: string;

    /**
     * Extra data, in the future will be passed from search API as is
     */
    extra: {
        /**
         * Type of the match between the search query and the entity
         */
        match_type: SearchMatchType;

        /**
         * Per-field reasons the entity matched the query (which fields hit and the
         * matched text). Currently only customer results carry these; absent for
         * other entity types.
         */
        match_reasons?: SearchMatchReason[];
    };
};

export type TapiIntegrationEvents = {
    /**
     * Triggered when a TAPI phone lookup is initiated — the integration page
     * opened with a phone number and the lookup started, before the result is
     * known. This is the clean attempt count (a logged-in rep actually ran a
     * lookup) and the denominator for resolution and match rate. Pairs with
     * `tapi_lookup_completed` via `lookup_id`.
     */
    tapi_lookup_started: {
        lookup_id: string;
    };

    /**
     * Triggered when a TAPI phone lookup resolves to a terminal customer
     * outcome: a match (redirects to the customer page) or a confirmed
     * no-match ("No customer found"). Errors, invalid numbers, hung queries,
     * and abandons deliberately do NOT fire this — they surface as a
     * `tapi_lookup_started` without a paired `tapi_lookup_completed`.
     */
    tapi_lookup_completed: {
        lookup_id: string;
        matched: boolean;
        // Mirrors the `phoneNumberLookup` output enum.
        match_type: 'exact' | 'partial' | 'multiple' | 'none';
        customer_id?: string;
        lookup_duration_ms?: number;
    };
};

export type SearchTrackingEvents = {
    /**
     * Triggered when the search UI activates (modal opens for the widget,
     * dropdown opens for the draft product search, popover opens for form
     * fields).
     */
    search_opened: SearchBasePayload;

    /**
     * Triggered once per unique server response. Carries the full results
     * array so data scientists can analyze what was shown without joining
     * to a separate stream.
     */
    search_request: SearchRequestPayload;

    /**
     * Triggered when a user clicks a result. Self-contained — includes the
     * clicked item fields and the `search_request_id` of the request that
     * produced it.
     */
    search_result_clicked: SearchClickPayload;
};

export type ExplorerViewChartEvents = {
    /**
     * Triggered when a user switches the revenue chart's metric tab
     * (Order Sum / Revenue / Margin). Used on both the home dashboard and
     * the customer insights page.
     */
    explorer_chart_tab_changed: {
        from: 'ORDER_SUM' | 'REVENUE' | 'MARGIN' | 'ORDER_SUM_MARGIN';
        to: 'ORDER_SUM' | 'REVENUE' | 'MARGIN' | 'ORDER_SUM_MARGIN';
    };
};

export type SettingsMenuEvents = {
    /**
     * Triggered when the consolidated settings menu opens, on any of its
     * three surfaces.
     */
    settings_menu_opened: {
        surface: 'sidebar' | 'rail' | 'mobile_sheet';
        /** Whether the menu showed the Organisation section — the rollout denominator. */
        hasOrganisation: boolean;
    };

    /**
     * Triggered when the user opens the tenant's external webshop in a new tab.
     */
    webshop_opened: {
        source: 'side_menu' | 'item_detail_page';
        /** `standalone` when Plato runs as an installed PWA. */
        display_mode: 'browser' | 'standalone';
    };

    /**
     * Triggered when the user picks a different language, from the settings
     * menu or from the standalone picker on the public pages (sign-in, 404).
     */
    language_switched: {
        from: string;
        to: string;
        source: 'settings_menu' | 'public_page';
    };

    /**
     * Triggered when the user picks their own default commercial figure or
     * reporting period in the settings menu. Until they do, the tenant default
     * applies — so the first event per user marks them overriding it.
     */
    commercial_default_changed: {
        setting: 'metric' | 'timeframe';
        from: string;
        to: string;
    };
};

export type LegacyLanguageUrlEvents = {
    /**
     * Triggered after a visit through a legacy /en or /de URL prefix. The
     * redirect strips the prefix without changing the language. The event
     * measures whether such links are still in circulation.
     */
    legacy_language_url_visited: {
        /** The language prefix the URL carried. */
        url_language: string;
        /** The path the user landed on, without the prefix. */
        path: string;
    };
};

/**
 * The entry point that opened the panel. `top_bar` is the mobile avatar in the
 * top bar — the desktop top-bar trigger is gone, the desktop entry is the
 * `fab`. `teaser` is the speech bubble attached to either of them.
 * `home_card` is a question sent from the Ask Plato card on the home dashboard.
 */
export type AiChatOpenedVia = 'fab' | 'top_bar' | 'teaser' | 'shortcut' | 'auto' | 'home_card';

/** `home_card`: each question from the home-dashboard card is a new topic. */
export type AiChatNewChatReason = 'user' | 'stale' | 'home_card';

/** Which trigger the teaser bubble decorates. */
export type AiChatTeaserPlacement = 'fab' | 'top_bar';

/**
 * Which copy affordance was used: the ghost button beside a bubble on hover
 * devices, the always-visible one in the action row under the latest answer,
 * or a long-press on the bubble on touch.
 */
export type AiChatCopiedVia = 'gutter' | 'action_row' | 'long_press';

export type AiChatMessageRating = 'like' | 'dislike' | 'none';

/**
 * Why the escalation was offered. A subset of `AiChatReplyStatus` plus `error`
 * for streams that died before the server assigned one — deliberately its own
 * union, so widening which outcomes get the button is a considered change here
 * rather than a silent side effect of the reply statuses growing.
 */
export type AiChatSupportTrigger = 'failure' | 'max_rounds_reached' | 'answered_without_tools' | 'error';

export type AiChatEvents = {
    /**
     * Triggered once per app load for a user who can reach the panel at all
     * (`enableAiChat`). The denominator for every other number
     * here: `$feature_flag_called` reports that the flag resolved, not that a
     * human had an entry point in front of them.
     *
     * Fired from the provider rather than from the FAB and the mobile trigger,
     * because both of those mount together on every screen — only CSS decides
     * which one is visible, so firing per component would double count.
     */
    ai_chat_entry_point_rendered: undefined;

    /** Triggered when the panel opens, from either entry point or the ⌘J/Ctrl+J shortcut. */
    ai_chat_opened: {
        opened_via: AiChatOpenedVia;
    };

    /**
     * Triggered when the panel closes, carrying how it was opened so entry
     * points can be compared on what happened next.
     */
    ai_chat_closed: {
        opened_via: AiChatOpenedVia;
    };

    /**
     * Triggered when a question is sent. The activation event of the feature.
     *
     * Carries the question text. The wording is also persisted in
     * `ai__chat_message` under tenant scope, but joining back to it needs
     * `conversation_id`, which is absent for the opening question of every
     * conversation — so the join fails on exactly the question worth reading.
     */
    ai_chat_message_submitted: {
        /**
         * Absent for the first question of a conversation: the id is minted by
         * the server, and the client only learns it once `create` resolves —
         * after the question was already sent.
         */
        conversation_id?: string;

        /** Zero-based index of the question within the conversation. */
        message_index: number;

        /**
         * The user pressed "Try again" on a failed reply. The retry reuses the
         * failed turn's `message_index`, so this flag is the only way to tell
         * the two submissions apart.
         */
        is_retry: boolean;

        /** Length of the plain text the composer submitted, before it becomes Lexical. */
        message_length_chars: number;

        /**
         * The question as typed, trimmed. Free text written by the user: it
         * names customers and quotes revenue figures, so it is tenant data
         * sitting in PostHog rather than in the app's own database.
         */
        message_text: string;
    };

    /**
     * Triggered when a like or dislike under an answer is pressed. The quality
     * signal of the feature: `ai_chat_message_submitted` counts questions asked,
     * this one counts answers worth reacting to.
     *
     * Carries no answer text: replies quote customers and revenue figures, and
     * the wording is already persisted in `ai__chat_message` under tenant scope.
     * Join on `conversation_id` and `message_index` for the content.
     */
    ai_chat_message_rated: {
        /**
         * The rating the answer carries after the press. Pressing the rating an
         * answer already has retracts it, which reports `none` — so the current
         * verdict is the latest event per (`conversation_id`, `message_index`),
         * not a sum over all of them.
         */
        rating: AiChatMessageRating;

        /**
         * Absent only for answers rated before the server minted the id, which
         * cannot happen today: the id arrives with the reply being rated.
         */
        conversation_id?: string;

        /** Zero-based index of the exchange this answer belongs to, matching its question. */
        message_index: number;

        /** Length of the answer as rendered, for comparing ratings against reply size. */
        response_length_chars: number;
    };

    /**
     * Triggered when a reply stream reaches its terminal `done` chunk, carrying
     * the outcome the server assigned it. Pairs with
     * `ai_chat_message_submitted`: a question with no matching reply event is a
     * stream that died before the server could report on it.
     *
     * Not an action event — it reports a system outcome, like
     * `stale_client_detected`. It fires once per question, on the terminal
     * chunk rather than on a lifecycle hook, so it needs no re-fire guard.
     */
    ai_chat_reply_shown: {
        /**
         * How the server judged the reply. Only `success` is unambiguously
         * good: `failure` and `interrupted` still reach the user as a normal
         * completed stream, so this property is the only way to tell a healthy
         * answer from a broken one.
         */
        status: AiChatReplyStatus;

        /** Always present here — the terminal chunk carries the id. */
        conversation_id: string;

        /** Zero-based index of the exchange, matching its question. */
        message_index: number;

        /** The persisted `ai__chat_message` id, for joining to the reply text. */
        message_id: string;

        /**
         * How many links to Plato pages the reply carries.
         */
        link_count: number;
    };

    /**
     * Triggered when a link to a Plato page inside a reply is followed. With
     * `link_count` on `ai_chat_reply_shown`, the click-through rate of the
     * page links the model adds. A click followed by a `$pageview` of the 404
     * page is a path the model got wrong.
     */
    ai_chat_link_clicked: {
        conversation_id?: string;

        /** Zero-based index of the exchange the link belongs to. */
        message_index: number;

        /** The reply's persisted `ai__chat_message` id, for joining to the reply text. */
        message_id: string;

        /** The path with entity IDs masked, e.g. `/customers/:id/info`. Never a concrete ID. */
        path: string;

        /** The link opens one entity rather than a list page. */
        has_params: boolean;

        /** A modifier or middle click let the browser open a tab; the chat stayed where it was. */
        new_tab: boolean;
    };

    /**
     * Triggered when the escalation offered under a bad reply is pressed. The
     * cost signal of the feature: `ai_chat_reply_shown` counts how often each
     * outcome happens, this one counts the ones that cost a support ticket.
     *
     * Compare the two per `trigger` to decide which outcomes deserve the
     * button — an outcome that is offered often and pressed rarely is noise.
     */
    ai_chat_support_requested: {
        /**
         * Which outcome put the button on screen. `error` is a stream that died
         * before the server could classify it, so it has no reply status.
         */
        trigger: AiChatSupportTrigger;

        /** Zero-based index of the exchange being escalated. */
        message_index: number;
    };

    /**
     * Triggered when the X on the teaser bubble is pressed. Opening the chat
     * retires the teaser too, but that shows up as `ai_chat_opened`; this is
     * the users who declined the invitation without ever looking.
     */
    ai_chat_teaser_dismissed: {
        placement: AiChatTeaserPlacement;
    };

    /**
     * Triggered when the history view replaces the chat. Pairs with
     * `ai_chat_history_closed` to tell browsing from resuming.
     */
    ai_chat_history_opened: {
        /** How many past conversations were listed. Absent while they were still loading. */
        conversation_count?: number;
    };

    /**
     * Triggered when the history view gives way to the chat again — by the
     * back button or by picking a conversation. A `resumed: false` close is a
     * user who looked and found nothing worth reopening.
     */
    ai_chat_history_closed: {
        resumed: boolean;
    };

    /**
     * Triggered when a past conversation is picked from history and its
     * transcript arrives. Not fired when the fetch fails — that goes to Sentry.
     */
    ai_chat_conversation_resumed: {
        conversation_id: string;

        /** Number of user and assistant messages in the loaded transcript. */
        message_count: number;

        /** How old the conversation was, in the buckets the history view groups by. */
        recency: ConversationRecencyGroup;
    };

    /**
     * Triggered when the plus button clears the transcript for a fresh
     * conversation. Without it, a resumed conversation that is replaced looks
     * the same as a freshly opened panel.
     */
    ai_chat_new_chat_started: {
        /** The conversation being left. Absent when nothing had been sent yet. */
        previous_conversation_id?: string;

        /** Messages on screen when it was cleared. */
        previous_message_count: number;

        /**
         * `user` for the header's new-chat button and the closed conversation's
         * start-new-chat action; `stale` when opening the chat onto a
         * conversation closed long enough ago to count as stale started a fresh
         * one (see `staleConversationAfterMs` in the provider).
         */
        reason: AiChatNewChatReason;
    };

    /**
     * Triggered when a reply ends its conversation because it sent the LLM
     * at least the server's `aiChatMaxContextTokens`.
     */
    ai_chat_context_limit_reached: {
        conversation_id: string;

        /** Questions asked in the conversation when it closed. */
        question_count: number;
    };

    /**
     * Triggered when a message's text is copied, through any of the copy
     * affordances. Alongside `ai_chat_message_rated`, a positive signal that an
     * answer was worth keeping. Carries no text, same as the rating event.
     */
    ai_chat_message_copied: {
        conversation_id?: string;

        /** Zero-based index of the exchange the copied message belongs to. */
        message_index: number;

        role: 'user' | 'assistant';

        via: AiChatCopiedVia;
    };
};

export type TenderWinEventPayload = {
    customer_id: string;
    /** Portal page of the award notice; identifies the card. */
    notice_url: string;
    /** `winner` won at least one lot; `bidder` bid and lost. */
    role: 'winner' | 'bidder';
    confidence: 'high' | 'medium';
    value_eur?: number;
    buyer: string;
};

/** Prototype: public-tender award cards on the customer page (RETAIN-1390). */
export type TenderWinEvents = {
    /** A tender-win card rendered for the customer. One event per card. */
    tender_win_viewed: TenderWinEventPayload;
    /** The rep opened the award notice on oeffentlichevergabe.de. */
    tender_win_notice_opened: TenderWinEventPayload;
    /** The rep rated the card; the card is then hidden on this device. */
    tender_win_rated: TenderWinEventPayload & { response: 'interested' | 'not_relevant' };
};

export type AnalyticsEvents = SalesInsightEvents &
    TenderWinEvents &
    ExplorerViewChartEvents &
    TransactionsEvents &
    DraftLineItemEvents &
    DraftHeaderEvents &
    TaskEvents &
    CustomerNoteEvents &
    SalesReportEvents &
    ProductDetailEvents &
    CustomerListEvents &
    EventEvents &
    VoiceInputEvents &
    FilterViewEvents &
    AIWritingEvents &
    TransactionDocumentEvents &
    HomeRecommendationsEvents &
    HomeDashboardEvents &
    DataFreshnessEvents &
    CrmFieldEvents &
    CustomerModeEvents &
    VersionEvents &
    ViewModeEvents &
    CustomerDetailEvents &
    GlobalActivityFeedEvents &
    TeamPageEvents &
    AttachmentEvents &
    SearchTrackingEvents &
    TapiIntegrationEvents &
    RecommendedCustomerEvents &
    LegacyLanguageUrlEvents &
    SettingsMenuEvents &
    AiChatEvents;

export type AnalyticsEventType = keyof AnalyticsEvents;
export type AnalyticsEventPayload<T extends AnalyticsEventType> = AnalyticsEvents[T];
